/**
 * Labels for the letter list, inbox, and open letter.
 * A name is shown only when the letter itself has it.
 */

import { officialCprDigits } from '@/lib/cpr';
import { organizationFieldsFromStored } from '@/lib/medcom/sorEntityName';

export type LetterParty = {
  name: string | null;
  sorId: string | null;
};

function writtenParty(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  return text || null;
}

/**
 * Afsender/modtager on a thread row. The stored letter row is used when it
 * has a name or SOR. Otherwise the opened bundle's organisations are used,
 * including a SOR with no name so the same lookup as the letter header runs.
 */
export function letterParty(row: LetterParty, bundle?: LetterParty | null): LetterParty {
  return {
    name: writtenParty(row.name) ?? writtenParty(bundle?.name),
    sorId: writtenParty(row.sorId) ?? writtenParty(bundle?.sorId),
  };
}

/**
 * Organisation on an assembled thread segment. A missing name/SOR is filled
 * from the matching letter payload: the Organization name, or `SOR <id>`.
 */
export function threadSegmentParty(
  sender: { organizationName?: string | null; sorId?: string | null },
  letterOrganization: string | null | undefined,
): { organizationName: string | null; sorId: string | null } {
  const stored = organizationFieldsFromStored(sender.organizationName, sender.sorId);
  if (stored.name || stored.sorId) {
    return { organizationName: stored.name, sorId: stored.sorId };
  }
  const fromLetter = organizationFieldsFromStored(letterOrganization, null);
  return { organizationName: fromLetter.name, sorId: fromLetter.sorId };
}

/**
 * Organisation from the active letter this segment belongs to. That bundle
 * wins over Communicate’s assembled name, which may have been merged from an
 * archived copy that reused the same Organization id.
 */
export function owningBundleThreadSegmentParty(
  sender: { organizationName?: string | null; sorId?: string | null },
  letterOrganization: string | null | undefined,
): { organizationName: string | null; sorId: string | null } {
  const fromLetter = organizationFieldsFromStored(letterOrganization, null);
  if (fromLetter.name || fromLetter.sorId) {
    return { organizationName: fromLetter.name, sorId: fromLetter.sorId };
  }
  const stored = organizationFieldsFromStored(sender.organizationName, sender.sorId);
  return { organizationName: stored.name, sorId: stored.sorId };
}

export function organizationOnLetter(
  name: string | null | undefined,
  sorId: string | null | undefined,
): string | null {
  const written = name?.trim() ?? '';
  if (written) return written;
  const sor = sorId?.trim() ?? '';
  if (!sor) return null;
  return `SOR ${sor}`;
}

/**
 * The subject when the letter has one. Otherwise the category name, marked
 * so the list can show it as a stand-in.
 */
export function topicStandIn(
  topic: string | null | undefined,
  categoryName: string | null | undefined,
): { text: string; standIn: boolean } | null {
  const subject = topic?.trim() ?? '';
  if (subject) return { text: subject, standIn: false };
  const category = categoryName?.trim() ?? '';
  if (category && category !== '—') return { text: category, standIn: true };
  return null;
}

/** Category is the title when present. Subject sits on the line below. */
export function letterHeadingParts(
  categoryLabel: string | null | undefined,
  topic: string | null | undefined,
  fallback: string,
): { title: string; subjectBelow: string | null } {
  const category = categoryLabel?.trim() || null;
  const subject = topic?.trim() || null;
  if (category && subject) return { title: category, subjectBelow: subject };
  if (category) return { title: category, subjectBelow: null };
  if (subject) return { title: subject, subjectBelow: null };
  return { title: fallback, subjectBelow: null };
}

/**
 * Letter-view identity badge. Official CPR (OID 1.2.208.176.1.2) is labelled
 * CPR. Any other patient identifier is Erstatnings-CPR.
 */
export function patientIdentityBadgeLabel(
  patient:
    | {
        cpr?: string | null;
        xEcpr?: string | null;
        dEcpr?: string | null;
      }
    | null
    | undefined,
): 'CPR' | 'Erstatnings-CPR' | null {
  if (!patient) return null;
  if (officialCprDigits(patient.cpr)) return 'CPR';
  if (patient.xEcpr || patient.dEcpr || patient.cpr) return 'Erstatnings-CPR';
  return null;
}
